import {
  Badge, Box, Button, Divider, Flex, Input, Link, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalHeader, ModalOverlay, Text, useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FiExternalLink } from "react-icons/fi";
import swal from "sweetalert";
import { api } from "../../../services/api";

const GREEN  = "#004a33";
const ORANGE = "#fc8f04";

function Section({ title, children }) {
  return (
    <Box mb="20px">
      <Text fontSize="11px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.07em" mb="10px">
        {title}
      </Text>
      {children}
    </Box>
  );
}

function InfoRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <Flex justify="space-between" align="center" py="5px" gap="10px" borderBottom="1px solid rgba(0,74,51,.05)">
      <Text fontSize="12px" color="gray.400" fontWeight="600">{label}</Text>
      <Text fontSize="12px" color="gray.700" fontWeight="700" textAlign="right">{value}</Text>
    </Flex>
  );
}

function money(cents) {
  return ((cents || 0) / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function moneyFromAmount(amount) {
  return Number(amount || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDate(val) {
  const d = new Date(val);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

const PAYMENT_STATUS_LABEL = {
  paid: "Pago",
  pending: "Pendente",
  cancelled: "Cancelado",
};

export function UserDetail({ userId, userName }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creditFormFor, setCreditFormFor] = useState(null);
  const [creditAmount, setCreditAmount] = useState("");
  const [submittingCredit, setSubmittingCredit] = useState(false);

  async function fetchDetail() {
    setLoading(true);
    try {
      const response = await api.get(`/api/v1/users/${userId}`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching user detail:", error);
      swal({ title: "Erro", text: "Não foi possível carregar os dados deste usuário.", icon: "error" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isOpen) fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  function openCreditForm(batchOrderId) {
    setCreditFormFor(batchOrderId);
    setCreditAmount("");
  }

  async function handleGrantCredit(batchOrderId) {
    const amount = parseFloat(creditAmount);
    if (!amount || amount <= 0) {
      swal({ title: "Atenção", text: "Informe um valor de crédito válido.", icon: "info" });
      return;
    }
    setSubmittingCredit(true);
    try {
      await api.post("/api/v1/credits", {
        credit: { user_id: userId, batch_order_id: batchOrderId, amount },
      });
      swal({ title: "Crédito gerado!", text: `${moneyFromAmount(amount)} adicionados para ${userName}.`, icon: "success" });
      setCreditFormFor(null);
      setCreditAmount("");
      await fetchDetail();
    } catch (error) {
      const message = error.response?.data?.error || "Não foi possível gerar o crédito.";
      swal({ title: "Erro", text: message, icon: "error" });
    } finally {
      setSubmittingCredit(false);
    }
  }

  const user = data?.user;
  const totalCredits = (data?.credits || []).reduce((sum, c) => sum + Number(c.amount || 0), 0);

  return (
    <>
      <Button
        size="xs" borderRadius="7px" px="10px" h="26px"
        bg={GREEN} color={ORANGE} fontWeight="700" fontSize="11px"
        _hover={{ opacity: 0.85 }} onClick={onOpen}
      >
        Ver detalhes
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl" scrollBehavior="inside">
        <ModalOverlay bg="rgba(0,0,0,.45)" backdropFilter="blur(2px)" />
        <ModalContent borderRadius="16px" overflow="hidden" mx="16px" maxH="85vh">
          <ModalHeader bg={GREEN} py="14px" px="20px" pr="44px">
            <Text fontSize="15px" fontWeight="800" color={ORANGE}>Usuário</Text>
            <Text fontSize="11px" color="rgba(255,255,255,.45)" fontWeight="500" mt="1px" noOfLines={1}>
              {userName} · #{userId}
            </Text>
          </ModalHeader>
          <ModalCloseButton color="rgba(255,255,255,.6)" _hover={{ color: "white", bg: "transparent" }} top="12px" right="14px" />

          <ModalBody px="20px" py="20px" bg="#fafbfc">
            {loading && (
              <Text fontSize="13px" color="gray.400" textAlign="center" py="30px">Carregando…</Text>
            )}

            {!loading && user && (
              <>
                <Section title="Dados para nota fiscal">
                  <Box bg="white" borderRadius="10px" p="12px 14px" border="1px solid rgba(0,74,51,.08)">
                    <InfoRow label="Nome completo" value={user.full_name} />
                    <InfoRow label="CPF" value={user.document_number} />
                    <InfoRow label="E-mail" value={user.email} />
                    <InfoRow label="Telefone" value={user.phone_number} />
                    <InfoRow
                      label="Endereço"
                      value={[user.address_street, user.address_number].filter(Boolean).join(", ") || "—"}
                    />
                    <InfoRow label="Complemento" value={user.address_complement} />
                    <InfoRow label="Bairro" value={user.address_neighborhood} />
                    <InfoRow
                      label="Cidade/UF"
                      value={[data.city?.name, data.state?.acronym || data.state?.name].filter(Boolean).join(" / ") || "—"}
                    />
                    <InfoRow label="CEP" value={user.address_postal_code} />
                    <InfoRow label="Cliente desde" value={fmtDate(user.created_at)} />
                  </Box>
                </Section>

                <Section title={`Créditos (${moneyFromAmount(totalCredits)} acumulados)`}>
                  {(data.credits || []).length === 0 ? (
                    <Text fontSize="12px" color="gray.400">Nenhum crédito registrado.</Text>
                  ) : (
                    <Box bg="white" borderRadius="10px" border="1px solid rgba(0,74,51,.08)" overflow="hidden">
                      {data.credits.map((c, i) => (
                        <Box key={c.id}>
                          <Flex justify="space-between" align="center" px="14px" py="8px">
                            <Text fontSize="12px" color="gray.600">{fmtDate(c.created_at)}</Text>
                            <Text fontSize="12px" fontWeight="700" color="#2f9e44">+{moneyFromAmount(c.amount)}</Text>
                          </Flex>
                          {i < data.credits.length - 1 && <Divider borderColor="rgba(0,74,51,.06)" />}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Section>

                <Section title="Pedidos">
                  {(data.order_total_info || []).length === 0 ? (
                    <Text fontSize="12px" color="gray.400">Nenhum pedido encontrado.</Text>
                  ) : (
                    <Box display="flex" flexDirection="column" gap="10px">
                      {data.order_total_info.map((entry, idx) => (
                        <Box key={entry.payment?.id || idx} bg="white" borderRadius="10px" p="12px 14px" border="1px solid rgba(0,74,51,.08)">
                          <Flex justify="space-between" align="center" mb="8px" wrap="wrap" gap="6px">
                            <Flex align="center" gap="8px">
                              <Text fontSize="12px" fontWeight="700" color={GREEN}>Pagamento #{entry.payment?.id}</Text>
                              <Badge
                                fontSize="9px" borderRadius="4px"
                                colorScheme={entry.payment?.status === "paid" ? "green" : entry.payment?.status === "cancelled" ? "red" : "orange"}
                              >
                                {PAYMENT_STATUS_LABEL[entry.payment?.status] || entry.payment?.status || "—"}
                              </Badge>
                              {entry.payment?.payment_method && (
                                <Text fontSize="11px" color="gray.400">{entry.payment.payment_method}</Text>
                              )}
                              <Text fontSize="12px" fontWeight="700" color="gray.600">{money(entry.payment?.price_in_cents)}</Text>
                            </Flex>
                            {entry.payment?.invoice_url && (
                              <Link href={entry.payment.invoice_url} isExternal fontSize="11px" color="#2980b9" fontWeight="700" display="flex" alignItems="center" gap="4px">
                                Fatura Iugu <FiExternalLink size={11} />
                              </Link>
                            )}
                          </Flex>

                          <Box display="flex" flexDirection="column" gap="8px">
                            {(entry.batch_orders || []).map((bo) => (
                              <Box key={bo.item.id} bg="rgba(0,74,51,.03)" borderRadius="8px" p="10px">
                                <Flex justify="space-between" align="flex-start" gap="8px" wrap="wrap">
                                  <Box>
                                    <Text fontSize="12px" fontWeight="700" color="gray.700" noOfLines={1}>
                                      {bo.product?.name || "Produto"} {bo.product?.brand ? `— ${bo.product.brand}` : ""}
                                    </Text>
                                    <Text fontSize="11px" color="gray.400">
                                      {bo.item.quantity} caixa{bo.item.quantity !== 1 ? "s" : ""} · {fmtDate(bo.item.created_at)}
                                      {" · "}
                                      <Text as="span" color={bo.item.paid ? "#2f9e44" : "#e67e22"} fontWeight="700">
                                        {bo.item.paid ? "Pago" : "Não pago"}
                                      </Text>
                                    </Text>
                                  </Box>
                                  <Flex align="center" gap="8px">
                                    <Text fontSize="12px" fontWeight="800" color={GREEN}>
                                      {moneyFromAmount(bo.current_total ?? bo.item.amount)}
                                    </Text>
                                    {bo.item.paid && (
                                      <Button
                                        size="xs" variant="outline" borderColor="rgba(0,74,51,.25)" color={GREEN}
                                        fontWeight="700" fontSize="10px" borderRadius="99px"
                                        onClick={() => openCreditForm(bo.item.id)}
                                      >
                                        Gerar crédito
                                      </Button>
                                    )}
                                  </Flex>
                                </Flex>

                                {creditFormFor === bo.item.id && (
                                  <Flex gap="8px" mt="8px" align="center">
                                    <Input
                                      size="sm" type="number" placeholder="Valor (R$)"
                                      value={creditAmount}
                                      onChange={e => setCreditAmount(e.target.value)}
                                      borderRadius="8px" bg="white" maxW="140px"
                                    />
                                    <Button
                                      size="sm" borderRadius="8px" bg={GREEN} color={ORANGE}
                                      fontWeight="700" fontSize="11px" isLoading={submittingCredit}
                                      onClick={() => handleGrantCredit(bo.item.id)}
                                    >
                                      Confirmar
                                    </Button>
                                    <Button
                                      size="sm" variant="ghost" fontSize="11px" color="gray.500"
                                      onClick={() => setCreditFormFor(null)}
                                    >
                                      Cancelar
                                    </Button>
                                  </Flex>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Section>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
